/**
 * Story `fe-be-monteur-s5d-offline-en-achtergrond` (S021.5d) deel C — het kanaal tussen
 * tabbladen: na inloggen (of hervatten) zendt de pagina `login`, na uitloggen `logout`, op de
 * `BroadcastChannel` `carculate-auth`.
 *
 * ⚠ Een bericht is GEEN bewijs (A-23): de ontvanger gebruikt nooit de `userId` uit het bericht,
 * alleen die uit zijn eigen ververste whoami. Het veld staat er alleen voor de leesbaarheid.
 * Zonder `BroadcastChannel` (oudere browsers, jsdom): niets, geen fout.
 */

export const AUTH_CHANNEL_NAME = 'carculate-auth';

export type AuthBroadcastType = 'login' | 'logout';

export interface AuthBroadcastMessage {
    type: AuthBroadcastType;
    /** Informatief; de ontvanger leest hem NIET (A-23). */
    userId?: string | null;
}

/**
 * Het id van DIT tabblad. Een `BroadcastChannel` levert ook af bij andere kanaal-objecten in
 * hetzelfde tabblad; zonder dit id zou een tabblad op zijn eigen bericht reageren (een extra whoami).
 */
export const OWN_TAB_ID = `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;

/**
 * Een bericht van het kanaal, of `null` als het onbekend of misvormd is (T7: negeren), of van dit
 * tabblad zelf komt.
 */
export function parseAuthMessage(data: unknown, ownTabId: string = OWN_TAB_ID): AuthBroadcastMessage | null {
    if (!data || typeof data !== 'object') return null;
    const { type, sender } = data as { type?: unknown; sender?: unknown };
    if (type !== 'login' && type !== 'logout') return null;
    if (sender === ownTabId) return null;
    return { type };
}

function openChannel(): BroadcastChannel | null {
    try {
        return typeof BroadcastChannel === 'function' ? new BroadcastChannel(AUTH_CHANNEL_NAME) : null;
    } catch {
        return null;
    }
}

/** Zendt een bericht naar de andere tabbladen. Faalt stil. */
export function postAuthMessage(type: AuthBroadcastType, userId?: string | null): void {
    const channel = openChannel();
    if (!channel) return;
    try {
        channel.postMessage({ type, userId: userId ?? null, sender: OWN_TAB_ID });
    } catch {
        /* kanaal onbruikbaar: het huidige gedrag (S021.4) blijft */
    } finally {
        try {
            channel.close();
        } catch {
            /* al dicht */
        }
    }
}

/** Luistert naar berichten van andere tabbladen. Geeft een opruimfunctie terug. */
export function subscribeAuthMessages(onMessage: (message: AuthBroadcastMessage) => void): () => void {
    const channel = openChannel();
    if (!channel) return () => undefined;
    const handler = (e: MessageEvent) => {
        const message = parseAuthMessage(e.data);
        if (message) onMessage(message);
    };
    channel.addEventListener('message', handler);
    return () => {
        channel.removeEventListener('message', handler);
        try {
            channel.close();
        } catch {
            /* al dicht */
        }
    };
}
